/// <reference types="vite/client" />

/**
 * logger.ts
 * 
 * Environment-gated logging utility for NatureNani.
 * All console output is suppressed in production (Vercel deployment).
 * In development (local Vite dev server), full logging is preserved.
 * 
 * Usage: import logger from '@/utils/logger';
 *        logger.log('message', data);
 */

import * as Sentry from '@sentry/react';

const isDev = import.meta.env.MODE === 'development';

const formatMessage = (args: unknown[]): string => {
  return args.map(arg => {
    if (arg instanceof Error) return arg.toString();
    if (typeof arg === 'object' && arg !== null) {
      try {
        return JSON.stringify(arg);
      } catch (e) {
        return '[Unserializable Object]';
      }
    }
    return String(arg);
  }).join(' ');
};

const logger = {
  log: (...args: unknown[]): void => {
    if (isDev) {
      console.log(...args);
    } else {
      Sentry.logger.info(formatMessage(args));
    }
  },
  warn: (...args: unknown[]): void => {
    if (isDev) {
      console.warn(...args);
    } else {
      Sentry.logger.warn(formatMessage(args));
    }
  },
  error: (...args: unknown[]): void => {
    if (isDev) {
      console.error(...args);
    } else {
      const formatted = formatMessage(args);
      Sentry.logger.error(formatted);
      
      // Extract the first original Error object (if any) to preserve the actual stack trace in Sentry
      const originalError = args.find(arg => arg instanceof Error) as Error | undefined;
      if (originalError) {
        Sentry.captureException(originalError, { extra: { context: formatted } });
      } else {
        Sentry.captureException(new Error(formatted));
      }
    }
  },
  info: (...args: unknown[]): void => {
    if (isDev) {
      console.info(...args);
    } else {
      Sentry.logger.info(formatMessage(args));
    }
  },
  debug: (...args: unknown[]): void => {
    if (isDev) console.debug(...args);
  },
  table: (...args: unknown[]): void => {
    if (isDev) console.table(...args);
  },
};

export default logger;
